// Type definitions for the Rumio Apps Platform contract.
//
// These shapes are mirrored by the Rumio platform itself. If a field here
// drifts from what Rumio sends, integration breaks — every change must be
// kept in sync with the Rumio contract docs (contract-v1.md).
import { z } from 'zod';
// ─── Session JWT (iframe embed token) ───
// Claims emitted by Rumio in the session JWT passed via `?rumio_session=`
// on iframe load. Signed HS256 with key = hex(SHA256(client_secret)).
export const SessionJwtClaimsSchema = z.object({
    iss: z.literal('rumio'),
    aud: z.string(),
    tenant: z.string(),
    sub: z.string().nullable(),
    role: z.string(),
    session_id: z.string(),
    // Viewer email (lowercased) for authenticated viewers; contract v1.x §3.3.
    email: z.string().optional(),
    iat: z.number(),
    exp: z.number(),
});
// ─── Manifest (published via POST /admin/v1/manifest) ───
export const ManifestNavEntrySchema = z.object({
    label: z.string().min(1),
    path: z.string().startsWith('/'),
    icon: z.string().optional(),
    required_plan_slug: z.string().optional(),
});
// Entitlement values can be:
//   boolean — feature on/off
//   number  — quota
//   null    — unlimited (only meaningful for quota-shaped entitlements)
//   string  — opaque grade tag
export const EntitlementValueSchema = z.union([
    z.boolean(),
    z.number(),
    z.null(),
    z.string(),
]);
export const ManifestSchema = z.object({
    nav: z.array(ManifestNavEntrySchema),
    entitlements: z.record(z.string(), z.record(z.string(), EntitlementValueSchema)),
});
// ─── Webhook envelope (POST /webhooks/rumio) ───
export const WebhookEnvelopeSchema = z.object({
    id: z.string().uuid(),
    event: z.string().min(1),
    tenant: z.string().min(1),
    // The shape of `data` is event-specific; consumers should narrow before use.
    data: z.unknown(),
    // Optional Rumio-emitted timestamp (seconds-precision unix epoch).
    ts: z.number().optional(),
});
// Subscription webhook payloads (the subset we actually consume).
export const SubscriptionEventDataSchema = z.object({
    rumio_user_id: z.string(),
    plan_slug: z.string().nullable().optional(),
    status: z.enum(['active', 'trialing', 'canceled', 'past_due', 'expired']),
    current_period_end: z.string().nullable().optional(),
});
// User webhook payloads.
export const UserEventDataSchema = z.object({
    id: z.string(),
    email: z.string().email().nullable().optional(),
    name: z.string().nullable().optional(),
    role: z.string().nullable().optional(),
});
// Canonical list of events this app subscribes to. Must match the array in
// scripts/register-with-rumio.ts.
export const SUBSCRIBED_EVENTS = [
    'user.created',
    'user.updated',
    'user.deleted',
    'user.role_changed',
    'subscription.created',
    'subscription.updated',
    'subscription.canceled',
    'subscription.renewed',
];
//# sourceMappingURL=rumio-types.js.map